import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import AccessGate from "@/components/AccessGate";
import Login from "@/pages/Login";
import MIDashboard from "@/pages/MIDashboard";
import Tasks from "@/pages/Tasks";
import Clients from "@/pages/Clients";
import ClientMaster from "@/pages/ClientMaster";
import Team from "@/pages/Team";
import Finance from "@/pages/Finance";
import FRTX from "@/pages/FRTX";
import CodingMemory from "@/pages/CodingMemory";
import QueryPackPublic from "@/pages/QueryPackPublic";
import Compliance from "@/pages/Compliance";
import Onboarding from "@/pages/Onboarding";
import OnboardingSign from "@/pages/OnboardingSign";
import BuildLog from "@/pages/BuildLog";
import Templates from "@/pages/Templates";
import Sales from "@/pages/Sales";
import TermsPublic from "@/pages/TermsPublic";
import FBSign from "@/pages/FBSign";
import FBSignPublic from "@/pages/FBSignPublic";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function Shell({ children }) {
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  return (
    <AccessGate
      allowed={user?.role === "admin"}
      title="Admin only"
      message="The Users section is available to administrators only."
    >
      {children}
    </AccessGate>
  );
}

function FinanceGate({ children }) {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.finance_enabled === true;
  return (
    <AccessGate
      allowed={allowed}
      title="Finance access required"
      message="You do not have permission to view the Finance section. An administrator can enable it for your account under Users → Finance."
    >
      {children}
    </AccessGate>
  );
}

function FRTXGate({ children }) {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.frtx_enabled === true;
  return (
    <AccessGate
      allowed={allowed}
      title="FRTX access required"
      message="You do not have permission to view FRTX. An administrator can enable it on your account under Users → FRTX."
    >
      {children}
    </AccessGate>
  );
}

function SalesGate({ children }) {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.sales_enabled === true;
  return (
    <AccessGate
      allowed={allowed}
      title="Sales access required"
      message="You do not have permission to view Sales. An administrator can enable it on your account under Users → Sales."
    >
      {children}
    </AccessGate>
  );
}

function OnboardingGate({ children }) {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.onboarding_enabled === true;
  return (
    <AccessGate
      allowed={allowed}
      title="Onboarding access required"
      message="You do not have permission to view Onboarding. An administrator can enable it on your account under Users → Onb."
    >
      {children}
    </AccessGate>
  );
}

function BuildLogGate({ children }) {
  const { user } = useAuth();
  return (
    <AccessGate
      allowed={user?.role === "admin" || user?.buildlog_enabled === true}
      title="Build Log access required"
      message="You do not have permission to use the Build Log. An administrator can enable it on your account under Users → Log."
    >
      {children}
    </AccessGate>
  );
}

function FBSignGate({ children }) {
  const { user } = useAuth();
  return (
    <AccessGate
      allowed={user?.role === "admin" || user?.fbsign_enabled === true}
      title="FBSign access required"
      message="You do not have permission to use FBSign. An administrator can enable it on your account under Users → Sign."
    >
      {children}
    </AccessGate>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/qp/:token" element={<QueryPackPublic />} />
          <Route path="/sign/:token" element={<OnboardingSign />} />
          <Route path="/terms/:token" element={<TermsPublic />} />
          <Route path="/fbsign/s/:token" element={<FBSignPublic />} />
          <Route path="/fbsign" element={<ProtectedRoute><Shell><FBSignGate><FBSign /></FBSignGate></Shell></ProtectedRoute>} />
          <Route path="/" element={<ProtectedRoute><Shell><MIDashboard /></Shell></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><Shell><Tasks /></Shell></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Shell><Clients /></Shell></ProtectedRoute>} />
          <Route path="/clients/master" element={<ProtectedRoute><Shell><AdminOnly><ClientMaster /></AdminOnly></Shell></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Shell><OnboardingGate><Onboarding /></OnboardingGate></Shell></ProtectedRoute>} />
          <Route path="/finance" element={<ProtectedRoute><Shell><FinanceGate><Finance /></FinanceGate></Shell></ProtectedRoute>} />
          <Route path="/frtx" element={<ProtectedRoute><Shell><FRTXGate><FRTX /></FRTXGate></Shell></ProtectedRoute>} />
          <Route path="/frtx/memory" element={<ProtectedRoute><Shell><FRTXGate><CodingMemory /></FRTXGate></Shell></ProtectedRoute>} />
          <Route path="/compliance" element={<ProtectedRoute><Shell><Compliance /></Shell></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute><Shell><AdminOnly><Team /></AdminOnly></Shell></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><Shell><Templates /></Shell></ProtectedRoute>} />
          <Route path="/sales" element={<ProtectedRoute><Shell><SalesGate><Sales /></SalesGate></Shell></ProtectedRoute>} />
          <Route path="/build-log" element={<ProtectedRoute><Shell><BuildLogGate><BuildLog /></BuildLogGate></Shell></ProtectedRoute>} />
          {/* Legacy redirects */}
          <Route path="/reports" element={<Navigate to="/" replace />} />
          <Route path="/communications" element={<Navigate to="/tasks" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
